import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnyoSortDirection } from 'projects/app-core/src/models/gridRequest';
import { LyfisService } from '../../../services/lyfis.service';
import {
  LyfisPersistedDiseaseScoreRow,
  LyfisPersistedDiseaseScoresFilter,
  LyfisPersistedDiseaseScoresGridRequest,
  LyfisPersistedDiseaseScoresSearch,
  LyfisPersistedDiseaseScoresSort,
} from '../models/lyfis-persisted-disease-scores-grid.models';

interface CheckboxOptions {
  label: string;
  value: string;
  checked: boolean;
}

@Component({
  selector: 'app-lyfis-view-snapshot-diseases',
  templateUrl: './lyfis-view-snapshot-diseases.component.html',
})
export class LyfisViewSnapshotDiseasesComponent {
  loading = false;
  rows: LyfisPersistedDiseaseScoreRow[] = [];
  page = 1;
  pageSize = 20;
  total = 0;
  snapshotId = '';
  searchDiseaseName = '';
  showTesting = false;

  diseaseIdCheckBoxOptions: CheckboxOptions[] = [];
  bandCheckBoxOptions: CheckboxOptions[] = [];
  statusCheckBoxOptions: CheckboxOptions[] = [];
  insightStatusCheckBoxOptions: CheckboxOptions[] = [];

  selectedDiseaseIds: string[] = [];
  selectedBands: string[] = [];
  selectedStatuses: string[] = [];
  selectedInsightStatuses: string[] = [];

  sortKey: LyfisPersistedDiseaseScoresSort = LyfisPersistedDiseaseScoresSort.CREATED_AT;
  sortDirection = AnyoSortDirection.DESC;

  sortOptions = [
    { label: 'Created At', value: LyfisPersistedDiseaseScoresSort.CREATED_AT },
    { label: 'DRS', value: LyfisPersistedDiseaseScoresSort.DRS_SCORE },
    { label: 'PDH', value: LyfisPersistedDiseaseScoresSort.PDH_SCORE },
    {
      label: 'Questionnaire Score',
      value: LyfisPersistedDiseaseScoresSort.QUESTIONNAIRE_SCORE,
    },
    {
      label: 'Lab Marker Score',
      value: LyfisPersistedDiseaseScoresSort.LAB_MARKERS_SCORE,
    },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private lyfisService: LyfisService,
  ) {
    this.snapshotId = this.route.snapshot.paramMap.get('snapshotId') || '';
    void this.fetch();
  }

  fetch(): Promise<void> {
    this.loading = true;
    const req: LyfisPersistedDiseaseScoresGridRequest = {
      page: this.page,
      pageSize: this.pageSize,
      export: false,
      searchQuery: {
        [LyfisPersistedDiseaseScoresSearch.DISEASE_NAME]:
          this.searchDiseaseName || undefined,
      },
      filters: {
        [LyfisPersistedDiseaseScoresFilter.DISEASE_ID]: this.selectedDiseaseIds.length
          ? this.selectedDiseaseIds
          : undefined,
        [LyfisPersistedDiseaseScoresFilter.BAND]: this.selectedBands.length
          ? this.selectedBands
          : undefined,
        [LyfisPersistedDiseaseScoresFilter.STATUS]: this.selectedStatuses.length
          ? this.selectedStatuses
          : undefined,
        [LyfisPersistedDiseaseScoresFilter.INSIGHTS_GENERATING_STATUS]:
          this.selectedInsightStatuses.length ? this.selectedInsightStatuses : undefined,
        [LyfisPersistedDiseaseScoresFilter.TEST_USERS]: [String(this.showTesting)],
      },
      sort: {
        [LyfisPersistedDiseaseScoresSort.CREATED_AT]:
          this.sortKey === LyfisPersistedDiseaseScoresSort.CREATED_AT
            ? this.sortDirection
            : undefined,
        [LyfisPersistedDiseaseScoresSort.DRS_SCORE]:
          this.sortKey === LyfisPersistedDiseaseScoresSort.DRS_SCORE
            ? this.sortDirection
            : undefined,
        [LyfisPersistedDiseaseScoresSort.PDH_SCORE]:
          this.sortKey === LyfisPersistedDiseaseScoresSort.PDH_SCORE
            ? this.sortDirection
            : undefined,
        [LyfisPersistedDiseaseScoresSort.QUESTIONNAIRE_SCORE]:
          this.sortKey === LyfisPersistedDiseaseScoresSort.QUESTIONNAIRE_SCORE
            ? this.sortDirection
            : undefined,
        [LyfisPersistedDiseaseScoresSort.LAB_MARKERS_SCORE]:
          this.sortKey === LyfisPersistedDiseaseScoresSort.LAB_MARKERS_SCORE
            ? this.sortDirection
            : undefined,
      },
    };
    return this.lyfisService
      .getLyfisPersistedSnapshotDiseaseScoresGrid(this.snapshotId, req)
      .toPromise()
      .then((res) => {
        this.rows = res?.data || [];
        this.total = res?.totalRecords || 0;
        
        this.setDiseaseIdFilterData(res?.filterOptions?.diseaseId);
        this.setBandFilterData(res?.filterOptions?.band);
        this.setStatusFilterData(res?.filterOptions?.status);
        this.setInsightStatusFilterData(res?.filterOptions?.insightsGeneratingStatus);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  setDiseaseIdFilterData(options?: string[]) {
    this.diseaseIdCheckBoxOptions = [];
    options?.map((data) => {
      this.diseaseIdCheckBoxOptions.push({
        checked: false,
        label: data,
        value: data,
      });
    });
  }

  setBandFilterData(options?: string[]) {
    this.bandCheckBoxOptions = [];
    options?.map((data) => {
      this.bandCheckBoxOptions.push({
        checked: false,
        label: data,
        value: data,
      });
    });
  }

  setStatusFilterData(options?: string[]) {
    this.statusCheckBoxOptions = [];
    options?.map((data) => {
      this.statusCheckBoxOptions.push({
        checked: false,
        label: data,
        value: data,
      });
    });
  }

  setInsightStatusFilterData(options?: string[]) {
    this.insightStatusCheckBoxOptions = [];
    options?.map((data) => {
      this.insightStatusCheckBoxOptions.push({
        checked: false,
        label: data,
        value: data,
      });
    });
  }

  diseaseIdFilterFn() {
    this.page = 1;
    void this.fetch();
  }

  diseaseIdFilterChangeFn($event: string[]) {
    this.selectedDiseaseIds = $event;
  }

  bandFilterFn() {
    this.page = 1;
    void this.fetch();
  }

  bandFilterChangeFn($event: string[]) {
    this.selectedBands = $event;
  }

  statusFilterFn() {
    this.page = 1;
    void this.fetch();
  }

  statusFilterChangeFn($event: string[]) {
    this.selectedStatuses = $event;
  }

  insightStatusFilterFn() {
    this.page = 1;
    void this.fetch();
  }

  insightStatusFilterChangeFn($event: string[]) {
    this.selectedInsightStatuses = $event;
  }

  testFilterFn() {
    this.page = 1;
    void this.fetch();
  }

  clearFilter() {
    this.searchDiseaseName = '';
    this.selectedDiseaseIds = [];
    this.selectedBands = [];
    this.selectedStatuses = [];
    this.selectedInsightStatuses = [];
    this.showTesting = false;

    this.diseaseIdCheckBoxOptions = [];
    this.bandCheckBoxOptions = [];
    this.statusCheckBoxOptions = [];
    this.insightStatusCheckBoxOptions = [];

    this.sortKey = LyfisPersistedDiseaseScoresSort.CREATED_AT;
    this.sortDirection = AnyoSortDirection.DESC;
    this.page = 1;
    void this.fetch();
  }

  openDetail(row: LyfisPersistedDiseaseScoreRow) {
    this.router.navigate([
      '/lyfis/view-scores/snapshots',
      this.snapshotId,
      'diseases',
      row.id,
      'trace-detail',
    ]);
  }

  onPageIndexChange(page: number) {
    this.page = page;
    void this.fetch();
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize = pageSize;
    this.page = 1;
    void this.fetch();
  }

  applyFilters() {
    this.page = 1;
    void this.fetch();
  }
}
